import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import {
  primaryDark,
  accentColor,
  buttonGradient,
  buttonGradientHover,
  buttonShadow,
} from "../../../constants/consts";

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`;

const CONTRACTS = [
  { value: "yes", label: "Var" },
  { value: "no", label: "Yok" },
  { value: "new", label: "Yeni Müşteri" },
];

const CONTRACT_TO_API = {
  Var: "yes",
  Yok: "no",
  Yeni: "new",
  "-": "no",
};

const EMPTY_FORM = {
  id: "",
  name: "",
  sunucuKodu: "",
  tc: "",
  vergiNo: "",
  contract: "no",
  email: "",
  phone: "",
};

export default function CompanyEditDialog({ open, company, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && company) {
      setForm({
        id: company.id ?? "",
        name: company.name ?? "",
        sunucuKodu: company.sunucuKodu ?? "",
        tc: company.tc ?? "",
        vergiNo: company.vergiNo ?? "",
        contract: CONTRACT_TO_API[company.contract] || "no",
        email: company.email ?? "",
        phone: company.phone ?? "",
      });
      setError("");
      setSaving(false);
    }
  }, [open, company]);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.sunucuKodu.trim()) {
      setError("Firma adı ve sunucu kodu zorunludur.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await adminService.editCompany(form);
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      setError("Şirket güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          animation: `${popIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1) both`,
          overflow: "hidden",
        },
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${primaryDark} 0%, ${accentColor} 100%)`,
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EditOutlined sx={{ fontSize: 20 }} />
          Sunucuyu Düzenle
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            label="Firma Adı *"
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            size="small"
          />
          <TextField
            label="Sunucu Kodu *"
            value={form.sunucuKodu}
            onChange={handleChange("sunucuKodu")}
            fullWidth
            size="small"
          />
          <TextField
            label="TC No"
            value={form.tc}
            onChange={handleChange("tc")}
            fullWidth
            size="small"
          />
          <TextField
            label="Vergi No"
            value={form.vergiNo}
            onChange={handleChange("vergiNo")}
            fullWidth
            size="small"
          />
          <TextField
            label="Telefon"
            value={form.phone}
            onChange={handleChange("phone")}
            fullWidth
            size="small"
          />
          <TextField
            label="Email"
            value={form.email}
            onChange={handleChange("email")}
            fullWidth
            size="small"
          />
          <TextField
            label="Sözleşme Durumu"
            value={form.contract}
            onChange={handleChange("contract")}
            select
            fullWidth
            size="small"
            sx={{ gridColumn: { xs: "1", sm: "1 / span 2" } }}
          >
            {CONTRACTS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {error && (
          <Typography
            sx={{
              mt: 2,
              color: "#B91C1C",
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            "&:hover": { transform: "translateY(-1px)" },
          }}
        >
          Vazgeç
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 700,
            background: buttonGradient,
            boxShadow: buttonShadow,
            "&:hover": { background: buttonGradientHover },
            "&:disabled": { background: "#c7c7c7" },
          }}
        >
          {saving ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Kaydet"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
